import { useEffect, useState } from 'react';
import { useParams, useMatch, generatePath, Outlet } from 'react-router-dom';
import {
  useQueryState,
  DateInputRange,
  PickerPosition,
  Tabs,
  useGetList,
  BaseTransaction,
  SchemaFilterType,
} from '@ui/src';
import { useLazyGetIndividualTransactionsWithWarningsQuery } from '../../individual/store/services/individual';
import { useLazyGetCorporateTransactionsWithWarningsQuery } from '../../corporate/store/services/corporate';
import { overviewAccountsSchema } from '../variables/overview-accounts';
import { overviewCardsSchema } from '../variables/overview-cards';
import { amlAccountsSchema } from '../../individual/variables/aml-accounts';
import { CustomerType } from '../types';
import { AmlAccountsStatementsTypes } from './types';

import cn from '../customers.module.css';

const AmlAccountsTransactionStatement = ({
  matchPath,
  transactionsPath,
  cardsTransactionsPath,
  title,
  customerType,
  accounts,
  cards,
  cardsEnabled,
  sendMessage,
}: AmlAccountsStatementsTypes): React.JSX.Element => {
  const { customerId } = useParams();
  const [dates, setDates] = useState<string[]>([]);

  const match = useMatch(matchPath);

  let schema: any = overviewAccountsSchema;

  if (match) {
    schema = overviewCardsSchema;
  }

  const { filters, onFilterChange } = useQueryState(schema);

  const [
    getIndividualTransactionsWithWarningsQuery,
    {
      isLoading: individualTransactionsIsLoading,
      isFetching: individualTransactionsIsFetching,
    },
  ] = useLazyGetIndividualTransactionsWithWarningsQuery();

  const [
    getCorporateTransactionsWithWarningsQuery,
    {
      isLoading: corporateTransactionsIsLoading,
      isFetching: corporateTransactionsIsFetching,
    },
  ] = useLazyGetCorporateTransactionsWithWarningsQuery();

  const { getListAction, setPage, onFilter, errorList } =
    useGetList<BaseTransaction>({
      getList:
        customerType === CustomerType.INDIVIDUAL
          ? getIndividualTransactionsWithWarningsQuery
          : getCorporateTransactionsWithWarningsQuery,
      onFilterChange,
      onSortChange: () => {},
      itemId: customerId,
      schema: amlAccountsSchema,
    });

  useEffect(() => {
    if (customerType && customerId) {
      getListAction({ ...filters });
    }
  }, [customerType, customerId]);

  return (
    <>
      <div className={cn.accountTransactionHistoryTitle}>
        <div className={cn.accountTransactionHistoryTitleLeft}>
          <div className={cn.accountTransactionHistoryTitleText}>{title}</div>
        </div>
        <div className={cn.accountTransactionHistoryTitleRight}>
          <DateInputRange
            placeholder="All time"
            className={cn.historyDatePicker}
            values={dates}
            onChangeRange={(values) => {
              setDates(values.map((item) => item.toISOString()));

              const nextFilters = {
                ...filters,
              };

              if (values) {
                nextFilters.transferDateFrom = {
                  value: [values[0].toISOString()],
                  type: SchemaFilterType.STRING,
                };

                if (values.length > 1) {
                  nextFilters.transferDateTo = {
                    value: [values[1].toISOString()],
                    type: SchemaFilterType.STRING,
                  };
                }
              }

              onFilter(nextFilters);
            }}
            onReset={() => {
              const nextFilters = { ...filters };

              delete nextFilters.transferDateFrom;
              delete nextFilters.transferDateTo;

              setDates([]);

              onFilter(nextFilters);
            }}
            withoutTwoPickers
            pickerPisition={PickerPosition.RIGHT}
          />
        </div>
      </div>
      {cardsEnabled && (
        <div className={cn.accountTransactionTabs}>
          <Tabs
            tabs={[
              {
                name: 'Account transactions',
                path: generatePath(transactionsPath, {
                  customerId,
                }),
              },
              {
                name: 'Card transactions',
                path: generatePath(cardsTransactionsPath, {
                  customerId,
                }),
              },
            ]}
            enableRouting
          />
        </div>
      )}
      <Outlet
        context={{
          accounts,
          cards,
          individualTransactionsIsLoading,
          individualTransactionsIsFetching,
          corporateTransactionsIsLoading,
          corporateTransactionsIsFetching,
          errorList,
          setPage,
          sendMessage,
        }}
      />
    </>
  );
};

export default AmlAccountsTransactionStatement;
